import React from 'react';
import styled from 'styled-components';

interface CyberButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

const CyberButton = ({ children, onClick, className = '' }: CyberButtonProps) => {
  return (
    <StyledWrapper className={className}>
      <button className="cyber-button" onClick={onClick}>
        <span className="cyber-button__content">{children}</span>
        <span className="cyber-button__glitch" aria-hidden>{children}</span>
        <span className="cyber-button__label" aria-hidden>P/T</span>
      </button>
    </StyledWrapper>
  );
}

const StyledWrapper = styled.div`
  .cyber-button {
    --primary: hsl(321, 70%, 69%); /* Neon pink */
    --primary-focus: hsl(321, 70%, 59%);
    --shadow-primary: hsl(197, 87%, 52%); /* Cyan */
    --color: hsl(0, 0%, 100%);
    --font-size: 20px;
    --shadow-primary-hue: 180;
    --shadow-secondary-hue: 60;
    --shadow-secondary: hsl(48, 96%, 53%); /* Yellow */
    --clip: polygon(12% 0, 95% 0, 100% 25%, 95% 95%, 90% 95%, 85% 95%, 85% 100%, 8% 100%, 0 85%);
    --border: 4px;
    --shimmy-distance: 5;
    --clip-one: polygon(0 2%, 100% 2%, 100% 95%, 95% 95%, 95% 90%, 85% 90%, 85% 95%, 8% 95%, 0 70%);
    --clip-two: polygon(0 78%, 100% 78%, 100% 100%, 95% 100%, 95% 90%, 85% 90%, 85% 100%, 8% 100%, 0 78%);
    --clip-three: polygon(0 44%, 100% 44%, 100% 54%, 95% 54%, 95% 54%, 85% 54%, 85% 54%, 8% 54%, 0 54%);
    --clip-four: polygon(0 0, 100% 0, 100% 0, 95% 0, 95% 0, 85% 0, 85% 0, 8% 0, 0 0);
    --clip-five: polygon(0 0, 100% 0, 100% 0, 95% 0, 95% 0, 85% 0, 85% 0, 8% 0, 0 0);
    --clip-six: polygon(0 40%, 100% 40%, 100% 85%, 95% 85%, 95% 85%, 85% 85%, 85% 85%, 8% 85%, 0 70%);
    --clip-seven: polygon(0 63%, 100% 63%, 100% 80%, 95% 80%, 95% 80%, 85% 80%, 85% 80%, 8% 80%, 0 70%);
    
    color: var(--color);
    text-transform: uppercase;
    font-size: var(--font-size);
    letter-spacing: 2px;
    position: relative;
    font-weight: 700;
    border: 0;
    min-width: 300px;
    height: auto;
    min-height: 75px;
    line-height: 1.2;
    padding: 1rem 40px;
    background: hsl(231, 15%, 18%); /* Dark blue-gray background */
    cursor: pointer;
    text-align: center;
    transition: all 0.2s ease;
    overflow: hidden;
    transform-origin: center;
    box-shadow: 0 0 15px var(--primary);
  }

  .cyber-button__content {
    position: relative;
    z-index: 2;
    text-shadow: 0 0 8px var(--color);
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  .cyber-button:hover {
    --primary: hsl(321, 70%, 59%);
    --font-size: 22px;
    transform: scale(1.05);
    letter-spacing: 3px;
    background: hsl(231, 15%, 22%);
    box-shadow: 
      0 0 20px var(--primary),
      0 0 30px var(--shadow-primary),
      0 0 40px var(--shadow-secondary);
  }

  .cyber-button:after,
  .cyber-button:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    clip-path: var(--clip);
    z-index: -1;
    transition: all 0.2s ease;
    background: linear-gradient(45deg, 
      var(--primary) 0%, 
      var(--shadow-primary) 50%, 
      var(--shadow-secondary) 100%
    );
    opacity: 0.5;
  }

  .cyber-button:before {
    background: var(--shadow-primary);
    transform: translate(var(--border), 0);
  }

  .cyber-button:hover:before {
    transform: translate(calc(var(--border) * 1.5), 0);
  }

  .cyber-button__glitch {
    position: absolute;
    top: calc(var(--border) * -1);
    left: calc(var(--border) * -1);
    right: calc(var(--border) * -1);
    bottom: calc(var(--border) * -1);
    background: var(--shadow-primary);
    text-shadow: 
      2px 2px var(--shadow-primary), 
      -2px -2px var(--shadow-secondary),
      0 0 20px var(--color);
    clip-path: var(--clip);
    animation: glitch 2s infinite;
    display: none;
    z-index: 1;
  }

  .cyber-button:hover .cyber-button__glitch {
    display: block;
  }

  .cyber-button__glitch:before {
    content: '';
    position: absolute;
    top: calc(var(--border) * 1);
    right: calc(var(--border) * 1);
    bottom: calc(var(--border) * 1);
    left: calc(var(--border) * 1);
    clip-path: var(--clip);
    background: var(--primary);
    z-index: -1;
  }

  .cyber-button__label {
    position: absolute;
    bottom: -15px;
    right: 8%;
    font-size: 10px;
    letter-spacing: 1px;
    opacity: 0;
    transition: opacity 0.2s ease;
    color: var(--primary);
    z-index: 2;
    text-shadow: 0 0 5px var(--primary);
  }

  .cyber-button:hover .cyber-button__label {
    opacity: 1;
  }

  @keyframes glitch {
    0% {
      clip-path: var(--clip-one);
    }
    2%, 8% {
      clip-path: var(--clip-two);
      transform: translate(calc(var(--shimmy-distance) * -1%), 0);
    }
    6% {
      clip-path: var(--clip-two);
      transform: translate(calc(var(--shimmy-distance) * 1%), 0);
    }
    9% {
      clip-path: var(--clip-two);
      transform: translate(0, 0);
    }
    10% {
      clip-path: var(--clip-three);
      transform: translate(calc(var(--shimmy-distance) * 1%), 0);
    }
    13% {
      clip-path: var(--clip-three);
      transform: translate(0, 0);
    }
    14%, 21% {
      clip-path: var(--clip-four);
      transform: translate(calc(var(--shimmy-distance) * 1%), 0);
    }
    25% {
      clip-path: var(--clip-five);
      transform: translate(calc(var(--shimmy-distance) * 1%), 0);
    }
    30% {
      clip-path: var(--clip-five);
      transform: translate(calc(var(--shimmy-distance) * -1%), 0);
    }
    35%, 45% {
      clip-path: var(--clip-six);
      transform: translate(calc(var(--shimmy-distance) * -1%));
    }
    40% {
      clip-path: var(--clip-six);
      transform: translate(calc(var(--shimmy-distance) * 1%));
    }
    50% {
      clip-path: var(--clip-six);
      transform: translate(0, 0);
    }
    55% {
      clip-path: var(--clip-seven);
      transform: translate(calc(var(--shimmy-distance) * 1%), 0);
    }
    60% {
      clip-path: var(--clip-seven);
      transform: translate(0, 0);
    }
    31%, 61%, 100% {
      clip-path: var(--clip-four);
    }
  }
`;

export default CyberButton;
